import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { combineLatest, from, merge, Observable, Subject, Subscription } from 'rxjs';

import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ManufacturerPrivilege, RetailerPrivilege } from 'helpers/team-user-roles';
import { prepareSearchQuery } from 'helpers/validate-search';
import {
    distinctUntilChanged,
    map,
    mapTo,
    mergeScan,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import { LanguageService, SearchParameters, SegmentService, UserService } from 'services';
import { TranslationMap } from 'services/language.service';
import { OrderHistoryFacet, OrderHistoryFacets } from 'services/product-search/facets-ui-mapper';
import { FilterSelectableItem } from 'services/product-search/filters-provider';
import { SkeletonService } from 'services/skeleton.service';
import { SpinnerService } from 'services/spinner.service';
import { TrackingEvents } from 'services/tracking/gtm';

import { formatPrice } from 'app/b2b-order/helpers/price';
import { FilterOptions, Search } from 'app/b2b-order/retailer-platform/order/types';
import { BackToTopButtonComponent } from 'app/components/back-to-top-button/back-to-top-button.component';
import { DownloadSelection } from 'app/content/components/download-selection-modal/download-selection.types';
import { DownloadSelectionService } from 'app/content/components/download-selection-modal/download-selection.service';
import { DownloadContentAlert, DownloadContentService } from 'app/content/services/download-content.service';
import { DownloadInitiator } from 'services/tracking/download-initiator';
import { retailersUrlPrefix } from 'app/retailer-platform/brand-shop/utils';
import { DropdownTreeMenu, FcpDropdownItem, FcpModalService, MenuListItem, Type } from 'fc-ui-components';
import { HistoricalOrder, OrderHistoryService } from '../../services/order-history';
import { menuActions } from './order-history.constants';

@Component({
    selector: 'fc-order-history',
    templateUrl: './order-history.component.html',
    styleUrls: ['./order-history.component.less', './table-mixin.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OrderHistoryComponent implements OnInit, AfterViewInit {
    @ViewChild('orderHistoryTopAnchor') topAnchor: ElementRef<HTMLDivElement>;
    @ViewChild(BackToTopButtonComponent) backToTop: BackToTopButtonComponent;

    /**
     * SHOW ONLY PREORDERS MODE
     * When mode is active, only preorders are shown. The Order Type filter is disabled.
     */
    @Input() showOnlyPreorders = false;

    public readonly downloadContentModalId = 'downloadSelectionModal';

    public isLoading: boolean;
    public search: Observable<{
        orders: HistoricalOrder[];
        total: number;
        nextId: string;
        query: string;
        filters: FilterOptions;
        facets: OrderHistoryFacets;
    }>;
    public filterForm: FormGroup;
    public searchForm: FormGroup;
    public displayedColumns: Observable<string[]>;
    public showClearAll: Observable<boolean>;
    public searchSubscription: Subscription;
    public retailersFilter: Observable<FilterSelectableItem[]>;
    public retailersFilterV2: Observable<DropdownTreeMenu>;
    public brandsFilter: Observable<FilterSelectableItem[]>;
    public brandsFilterV2: Observable<DropdownTreeMenu>;
    public selectedBrandFilters: Observable<string[]>;
    public selectedRetailersFilters: Observable<string[]>;
    public orderDatesFilter: Observable<FilterSelectableItem[]>;
    public orderTypesFilter: Observable<FilterSelectableItem[]>;
    public isDownloadProductDataEnabled: Observable<boolean>;
    public menuList: Observable<MenuListItem[]>;
    public menuKeyTranslationsMap: Observable<TranslationMap>;
    public clearAllTrigger: Subject<any> = new Subject();
    private loadMoreClick = new Subject<void>();

    public openDownloadOrderDetailsModal: Subject<HistoricalOrder> = new Subject<HistoricalOrder>();
    public closeDownloadOrderDetailsModal: Subject<any> = new Subject<any>();

    public showOrderDetails: boolean;
    public downloadContentAlert: Observable<DownloadContentAlert>;
    public closeContentAlert: Subject<DownloadContentAlert> = new Subject<DownloadContentAlert>();
    public readonly fcpAlertType = Type;

    constructor(
        private userService: UserService,
        private orderHistoryService: OrderHistoryService,
        private router: Router,
        private spinnerService: SpinnerService,
        private skeletonService: SkeletonService,
        private activatedRoute: ActivatedRoute,
        private segmentService: SegmentService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private downloadContentService: DownloadContentService,
        private downloadSelectionService: DownloadSelectionService,
        private fcpModalService: FcpModalService
    ) {
        this.spinnerService.initialize();
        this.skeletonService.initialize();
        this.searchForm = this.formBuilder.group({
            search: new FormControl(null)
        });
    }

    async ngOnInit() {
        this.showOrderDetails = this.getShowOrderDetails();
        this.skeletonService.turnOnSkeleton();

        const displayedColumnsForRetailer = ['orderNumber', 'date', 'brands', 'store', 'type', 'placedBy'];
        const displayedColumnsForManufacturer = [
            'orderNumber',
            'date',
            'retailer',
            'store',
            'placedBy',
            'gln',
            'type',
            'brand'
        ];

        if (this.showOrderDetails) {
            displayedColumnsForRetailer.push('articles', 'totalPP');
            displayedColumnsForManufacturer.push('articles', 'totalPP');
        }

        displayedColumnsForRetailer.push('actions');
        displayedColumnsForManufacturer.push('actions');
        const displayedColumns = this.isRetailer ? displayedColumnsForRetailer : displayedColumnsForManufacturer;
        this.displayedColumns = from([displayedColumns]);

        this.menuKeyTranslationsMap = this.languageService
            .translateObservable([
                'ORDER_HISTORY.ACTIONS.DOWNLOAD_ORDER_OVERVIEW_BUTTON',
                'ORDER_HISTORY.ACTIONS.DOWNLOAD_PRODUCT_DATA_BUTTON'
            ])
            .pipe(take(1));

        const searchParameters: Observable<SearchParameters> = this.activatedRoute.queryParamMap.pipe(
            map((paramMap) => this.orderHistoryService.mapUrlParams(paramMap))
        );

        this.filterForm = this.formBuilder.group({
            brands: new FormControl([]),
            retailers: new FormControl([]),
            orderDates: new FormControl(null),
            orderTypes: new FormControl([])
        });

        // Initialize Observable properties for selected filters to fix signal compatibility
        this.selectedBrandFilters = this.filterForm.controls.brands.valueChanges.pipe(
            startWith(this.filterForm.controls.brands.value || []),
            distinctUntilChanged(),
            shareReplay(1)
        );

        this.selectedRetailersFilters = this.filterForm.controls.retailers.valueChanges.pipe(
            startWith(this.filterForm.controls.retailers.value || []),
            distinctUntilChanged(),
            shareReplay(1)
        );

        this.setFilterList(searchParameters);

        this.searchSubscription = searchParameters.subscribe((parameters) => {
            /**
             * SHOW ONLY PREORDERS MODE
             * When mode is active, add preorder type on initial load.
             */
            if (this.showOnlyPreorders) {
                parameters.filters.orderTypes = [ClientType.FcPlatformPreorder];
            }

            this.searchForOrders(parameters);

            this.searchForm.controls.search.setValue(parameters.query);
        });

        this.showClearAll = searchParameters.pipe(
            map(({ query, filters }) => {
                const appliedFilters = [..._.keys(filters)];
                return !_.isEmpty(appliedFilters) || !_.isEmpty(query);
            })
        );

        this.clearAllTrigger.subscribe(() => {
            const newSearchParameters: Partial<SearchParameters> = {
                query: '',
                filters: {
                    retailers: [],
                    brands: [],
                    orderTypes: [],
                    orderDates: null
                }
            };

            /**
             * SHOW ONLY PREORDERS MODE
             * When mode is active, add preorder type even after clearing all filters.
             */
            if (this.showOnlyPreorders) {
                newSearchParameters.filters.orderTypes = [ClientType.FcPlatformPreorder];
            }

            this.applySearchParameters(newSearchParameters);
            this.filterForm.patchValue({ orderDates: null });
            this.searchForm.controls.search.setValue('');
            this.filterForm.controls.brands.setValue([]);
            this.filterForm.controls.retailers.setValue([]);
            this.segmentService.track('Clear all filters', { product: 'reorder' });
        });

        this.downloadContentAlert = merge(this.downloadContentService.downloadContentAlert, this.closeContentAlert);
    }

    ngAfterViewInit(): void {
        this.backToTop.initializeBackToTopButton(this.topAnchor);
    }

    loadMore() {
        this.loadMoreClick.next();
    }

    getBrandNameBadgesForOrder = _.memoize((order: HistoricalOrder): Array<string> => {
        const maxNumberOfBrandsToDisplay = 2;
        const maxBrandNameLength = 16;

        const brands = Array.from(new Set(order.brands.map((brand) => brand.name)));
        const displayBadges =
            brands.length > maxNumberOfBrandsToDisplay
                ? brands.slice(0, maxNumberOfBrandsToDisplay).concat(`+${brands.length - maxNumberOfBrandsToDisplay}`)
                : brands;

        if (displayBadges.length) {
            const hasMultipleBrands = displayBadges.length > 1;
            const exceedsMaxBrandNameLength = displayBadges[0].length > maxBrandNameLength * 2;

            if (hasMultipleBrands || exceedsMaxBrandNameLength) {
                return _.map(displayBadges, (brand) =>
                    brand.length > maxBrandNameLength ? `${brand.substring(0, maxBrandNameLength - 3)}...` : brand
                );
            }
        }

        return displayBadges;
    });

    public routeToOrderDetails(historicalOrder: HistoricalOrder) {
        if (!this.showOrderDetails) {
            return;
        }
        const isAttemptingTextSelection = window.getSelection().toString().length > 0;
        if (isAttemptingTextSelection) {
            return;
        }

        const { orderId, clientType } = historicalOrder;
        const isPreorder = clientType === ClientType.FcPlatformPreorder || clientType === ClientType.Showroom;
        const queryParams = isPreorder ? { type: clientType } : {};

        if (this.isRetailer) {
            this.router.navigate([retailersUrlPrefix, 'order-history', 'details', orderId], { queryParams });
        } else {
            this.router.navigate([location.pathname, orderId], { queryParams });
        }
    }

    public getMenuListForOrder(): Observable<MenuListItem[]> {
        return this.menuKeyTranslationsMap.pipe(
            map((translationMap) => [
                {
                    value: menuActions.DOWNLOAD_ORDER_OVERVIEW,
                    text: translationMap['ORDER_HISTORY.ACTIONS.DOWNLOAD_ORDER_OVERVIEW_BUTTON']
                },
                {
                    value: menuActions.DOWNLOAD_PRODUCT_DATA,
                    text: translationMap['ORDER_HISTORY.ACTIONS.DOWNLOAD_PRODUCT_DATA_BUTTON']
                }
            ])
        );
    }

    public getMenuListForOrderMemoized = _.memoize(this.getMenuListForOrder);

    public menuItemTriggered(item: MenuListItem, order: HistoricalOrder) {
        if (item.value === menuActions.DOWNLOAD_ORDER_OVERVIEW) {
            this.openDownloadOrderDetailsModal.next(order);
        }
        if (item.value === menuActions.DOWNLOAD_PRODUCT_DATA) {
            this.downloadContentService.setOrder(order);
            this.downloadSelectionService.setPendingContext({
                type: 'order',
                orderId: order.orderId,
                orderType: order.type,
                initiator: DownloadInitiator.OrderHistory
            });
            this.fcpModalService.open(this.downloadContentModalId);
        }
    }

    public onDownloadSelectionConfirmed(selection: DownloadSelection) {
        this.downloadSelectionService.executeDownload(selection);
    }

    public getOrderTotalPrice = _.memoize((order) =>
        formatPrice(order.price.value, order.price.currency, this.userService.getPreferredLanguage())
    );

    public getOrderTypeName(clientType: ClientType) {
        return this.orderHistoryService.getOrderTypeName(clientType);
    }

    public get isRetailer() {
        return this.userService.isRetailer();
    }

    public get showSpinner() {
        return this.spinnerService.isSpinnerVisible;
    }

    public get showSkeleton() {
        return this.skeletonService.isSkeletonVisible;
    }

    public handleOnCloseFilter(selectedValues: any[] | FcpDropdownItem, applyFilterFunction) {
        const values = Array.isArray(selectedValues) ? _.map(selectedValues, 'value') : selectedValues.value;
        applyFilterFunction.bind(this)(values);
    }

    public handleOnCloseOrderDateFilter(selectedValue: FcpDropdownItem) {
        if (selectedValue) {
            this.applyOrderDateFilter(selectedValue.value);
        }
    }

    public applyBrandsFilter(brands: any[]): void {
        this.applySearchParameters({ filters: { brands } });
    }

    public applyRetailersFilter(retailers: any[]): void {
        this.applySearchParameters({ filters: { retailers } });
    }

    public applyOrderTypesFilter(orderTypes: any[]): void {
        this.applySearchParameters({ filters: { orderTypes } });
    }

    public applyOrderDateFilter(orderDate: string): void {
        const value = orderDate === _.last(orderDates).value ? null : orderDate;
        if (!value) {
            this.filterForm.patchValue({ orderDates: null });
        }
        this.applySearchParameters({ filters: { orderDates: value } });
    }

    public setFilterSubscription(filter, formControl, filterType = FilterType.MultiSelect) {
        const initialFilterValue = filterType === FilterType.MultiSelect ? [] : null;
        filter.subscribe((filterSelectableItem) => {
            if (_.isEmpty(filterSelectableItem)) {
                formControl.setValue(initialFilterValue);
                formControl.disable();
            } else {
                if (formControl.disabled) {
                    formControl.enable();
                }
                if (filterType === FilterType.MultiSelect) {
                    formControl.setValue(_.filter(filterSelectableItem, 'checked'));
                } else {
                    const selectedValue = _.find(filterSelectableItem, 'checked');
                    if (selectedValue) {
                        formControl.setValue(selectedValue.value);
                    }
                }
            }
        });
    }

    public getSelectedFilters(filters, facets) {
        return _.chain(_.values(filters))
            .filter({ checked: true })
            .map((filter) => {
                const facet = facets.find((f) => f._id === filter.value);
                return {
                    ...filter,
                    text: facet?.name ? facet.name[0] : filter.text
                };
            })
            .value();
    }

    public onArticleSearch(query: string) {
        this.applySearchParameters({ query: prepareSearchQuery(query) });
    }

    public getSearchBarPlaceholder() {
        return this.isRetailer ? 'ORDER_HISTORY_OVERVIEW.SEARCH_RETAILER' : 'ORDER_HISTORY_OVERVIEW.SEARCH_BRAND';
    }

    public resetAllFilters() {
        this.clearAllTrigger.next({});
    }

    public onRemoveSearchQuery() {
        this.searchForm.controls.search.setValue('');
        this.applySearchParameters({ query: null, filters: {} });
    }

    public onRemoveBrandsFilter(searchValue: Search, brandsValue: string): void {
        this.onRemoveFilter(searchValue, brandsValue, 'brands', this.applyBrandsFilter.bind(this));
        const filterBrands = this.filterForm.controls.brands.value.filter((brandId) => brandId !== brandsValue);
        this.filterForm.controls.brands.setValue(filterBrands);
    }

    public onRemoveRetailersFilter(searchValue: Search, retailerValue: string): void {
        const filterRetailers = this.filterForm.controls.retailers.value.filter(
            (retailer) => retailer !== retailerValue
        );
        this.filterForm.controls.retailers.setValue(filterRetailers);
        this.onRemoveFilter(searchValue, retailerValue, 'retailers', this.applyRetailersFilter.bind(this));
    }

    public onRemoveOrderTypesFilter(searchValue: Search, brandsValue: string): void {
        this.onRemoveFilter(searchValue, brandsValue, 'orderTypes', this.applyOrderTypesFilter.bind(this));
    }

    public onRemoveOrderDatesFilter(): void {
        this.applyOrderDateFilter(null);
    }

    public searchForOrders(searchParameters) {
        const loadOrdersTrigger = this.loadMoreClick.pipe(mapTo(null), startWith(null));

        this.search = loadOrdersTrigger.pipe(
            tap(() => {
                if (!this.showSkeleton) {
                    this.spinnerService.turnOnSpinner();
                }
            }),
            mergeScan(
                ({ nextId, orders: currentOrders }) =>
                    this.orderHistoryService
                        .getOrders({
                            ...searchParameters,
                            nextId
                        })
                        .pipe(
                            map(({ orders: newOrders, nextId, facets }) => ({
                                orders: [...currentOrders, ...newOrders],
                                nextId,
                                ...searchParameters,
                                facets
                            }))
                        ),
                { nextId: '', orders: [] as HistoricalOrder[], ...searchParameters }
            ),
            tap(() => {
                this.spinnerService.turnOffSpinner();
                this.skeletonService.turnOffSkeleton();
            }),
            shareReplay()
        );
    }

    public setFilterList(searchParameters: Observable<SearchParameters>) {
        const allSearch = this.orderHistoryService.getOrders({ query: null, filters: {}, pagination: null }).pipe(
            map(({ facets }) => ({
                ...facets,
                // Temporary fix allowing Retailer filter to work with empty retailer facets from backend
                retailers: facets.retailers.filter(
                    ({ _id, name }: OrderHistoryFacet) =>
                        !_.isNull(_id) && _.isArray(name) && name.every((n) => !_.isEmpty(n))
                )
            })),
            shareReplay()
        );

        const searchParams = combineLatest([searchParameters, allSearch]);

        this.retailersFilter = searchParams.pipe(
            switchMap(([searchParams, facets]) =>
                this.orderHistoryService.mapFacetToRetailersFilter(facets.retailers, searchParams.filters.retailers)
            ),
            shareReplay()
        );

        this.retailersFilterV2 = searchParams.pipe(
            switchMap(([searchParams, facets]) => {
                if (!_.isEmpty(searchParams.filters?.retailers)) {
                    this.filterForm.controls.retailers.setValue(searchParams.filters.retailers);
                }
                return this.orderHistoryService.mapFacetToRetailersFilterV2(
                    facets.retailers,
                    searchParams.filters.retailers
                );
            }),
            shareReplay()
        );

        this.brandsFilter = searchParams.pipe(
            switchMap(([searchParams, facets]) =>
                this.orderHistoryService.mapFacetToBrandsFilter(facets.brands, searchParams.filters.brands)
            ),
            shareReplay()
        );

        this.brandsFilterV2 = searchParams.pipe(
            switchMap(([searchParams, facets]) => {
                if (!_.isEmpty(searchParams.filters?.brands)) {
                    this.filterForm.controls.brands.setValue(searchParams.filters.brands);
                }
                return this.orderHistoryService.mapFacetToBrandsFilterV2(facets.brands, searchParams.filters.brands);
            }),
            shareReplay()
        );

        this.orderTypesFilter = searchParams.pipe(
            switchMap(([searchParams, facets]) =>
                this.orderHistoryService.mapFacetToOrderTypesFilter(facets.orderTypes, searchParams.filters.orderTypes)
            ),
            shareReplay()
        );

        this.orderDatesFilter = combineLatest([searchParameters, this.orderDateFacets]).pipe(
            switchMap(([searchParams, orderDateFacets]) =>
                this.orderHistoryService.mapFacetToOrderDatesFilter(orderDateFacets, searchParams.filters.orderDates)
            ),
            shareReplay()
        );

        this.setFilterSubscription(this.orderDatesFilter, this.filterForm.controls.orderDates, FilterType.SingleSelect);
        this.setFilterSubscription(this.orderTypesFilter, this.filterForm.controls.orderTypes);
    }

    public get orderDates() {
        return this.languageService.translateObservable(orderDates.map((orderDate) => orderDate.label)).pipe(
            map((translatedKeys) =>
                orderDates.map((orderDate) => ({
                    ...orderDate,
                    label: translatedKeys[orderDate.label]
                }))
            )
        );
    }

    public get orderDateFacets() {
        return this.orderDates.pipe(
            map((orderDates) =>
                orderDates.map((orderDate) => ({
                    _id: orderDate.value,
                    name: [orderDate.label]
                }))
            )
        );
    }

    public showEmailIcon(order: any): boolean {
        return order.orderingUser?.email && order.orderingUser.organizationId !== this.userService.getOrganizationId();
    }

    public redirectToEmail($event, order: any) {
        $event.stopPropagation();
        window.location.href = `mailto:${order.orderingUser.email}?subject=${order.orderNumber}`;
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public handleOnSelectBrandFilter(selectedValues) {
        this.filterForm.controls.brands.setValue(selectedValues);
        this.applyBrandsFilter(selectedValues);
    }

    public handleOnSelectRetailerFilter(selectedValues) {
        this.filterForm.controls.retailers.setValue(selectedValues);
        this.applyRetailersFilter(selectedValues);
    }

    public closeSnackbar() {
        this.closeContentAlert.next({ display: false });
    }

    public totalArticles = _.memoize((order: HistoricalOrder): number => {
        const groupByColorGroupIdAndStyleGroupId = _.groupBy(order.productItems, (item) => {
            return `${item.colorGroupId || ''}-${item.styleGroupId || ''}`;
        });
        return Object.values(groupByColorGroupIdAndStyleGroupId).length;
    });

    public get requiredPrivileges() {
        return {
            retailer: [RetailerPrivilege.Admin, RetailerPrivilege.Buyer],
            manufacturer: [ManufacturerPrivilege.Admin, ManufacturerPrivilege.SalesAgent]
        };
    }

    private applySearchParameters(searchParameters: Partial<SearchParameters>) {
        this.orderHistoryService.navigate({
            ...searchParameters
        });
        this.trackSearchParameterChanges(searchParameters);
    }

    private trackSearchParameterChanges(searchParameters: Partial<SearchParameters>) {
        this.segmentService.track('Order search', { queryParams: searchParameters });

        if (searchParameters?.query) {
            return;
        }

        const { brands, retailers, orderTypes, orderDates } = {
            ...searchParameters?.filters
        };

        this.segmentService.trackGtm(TrackingEvents.FILTER_INTERACTION, {
            page_type: 'order_history',
            filter_brand: brands,
            filter_retailer: retailers,
            filter_order_type: orderTypes,
            filter_order_date: orderDates
        });
    }

    private onRemoveFilter(
        searchValue: Search,
        value: string,
        key: string,
        applyFilterFunc: (values: string[]) => void
    ): void {
        const values = _.filter(searchValue.filters[key], (filteredValue) => filteredValue !== value);
        applyFilterFunc(values);
    }

    private getShowOrderDetails() {
        if (this.userService.isRetailer()) {
            return this.userService.hasAtLeastOnePrivilege(this.requiredPrivileges.retailer);
        } else {
            return (
                this.userService.hasAtLeastOnePrivilege(this.requiredPrivileges.manufacturer) &&
                !this.userService.hasAtLeastOnePrivilege([ManufacturerPrivilege.BrandManager])
            );
        }
    }

    protected readonly ClientType = ClientType;
}

const orderDates: Array<FcpDropdownItem> = [
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.LAST_WEEK',
        value: 'one_week'
    },
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.LAST_TWO_WEEKS',
        value: 'two_weeks'
    },
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.LAST_MONTH',
        value: 'one_month'
    },
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.LAST_THREE_MONTHS',
        value: 'three_months'
    },
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.LAST_YEAR',
        value: 'one_year'
    },
    {
        label: 'ORDER_HISTORY_OVERVIEW.ORDER_DATES_FILTER.ALL_TIME',
        value: 'all_time'
    }
];

export enum OrderType {
    Reorder = 'BRAND_PANEL.REORDER_PLATFORM',
    AutoRestock = 'ORDER_AUTO_RESTOCK.TITLE',
    SmartReplenishment = 'ORDER_SMART_REPLENISHMENT.TITLE',
    Suggestions = 'ORDER_SUGGESTIONS.TITLE',
    Clara = 'CLARA_PAGE.TITLE',
    Preorder = 'BRAND_PANEL.PREORDER_PLATFORM',
    NonPlatformOrder = 'ORDER_HISTORY_OVERVIEW.NON_PLATFORM_ORDER',
    Showroom = 'ORDER_HISTORY_OVERVIEW.SHOWROOM_ORDER'
}

export enum ClientType {
    FcPlatform = 'fc-platform',
    SmartReplenishment = 'sales-dashboard-auto-replenishment',
    Suggestions = 'smartrep-suggestion',
    AutoRestock = 'smartrep-auto-restock',
    FcPlatformPreorder = 'fc-platform-preorder',
    Clara = 'clara',
    Showroom = 'showroom'
}

enum FilterType {
    MultiSelect = 'multi-select',
    SingleSelect = 'single-select'
}
